import { z } from "zod";

export const fileKindSchema = z.enum(["PROSTHESIS_ATTACHMENT", "GENERIC"]);
export type FileKind = z.infer<typeof fileKindSchema>;

export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  // Escaneamento intraoral (iTero, 3Shape, Medit etc) e export de modelo 3D — sem assinatura de
  // bytes conhecida (ver matchesDeclaredType), então essa allowlist é só a primeira barreira.
  "model/stl",
  "application/sla",
  "application/zip",
  "application/x-zip-compressed",
  "application/octet-stream",
] as const;

export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024; // 50MB — scan/STL de arcada completa passa de 20MB fácil.
/** Teto de upload single-shot pro Drive genérico — qualquer tipo de arquivo, tamanho moderado.
 *  Acima disso, o cliente usa o fluxo multipart (fileMultipartInitInputSchema). */
export const MAX_GENERIC_SINGLE_SHOT_BYTES = 50 * 1024 * 1024; // 50MB
/** Teto do fluxo multipart — cobre STL/modelos 3D e outros arquivos grandes do Drive. */
export const MAX_MULTIPART_BYTES = 500 * 1024 * 1024; // 500MB
export const MULTIPART_PART_SIZE_BYTES = 8 * 1024 * 1024; // 8MB por parte

const fileRequestUploadBaseFields = {
  fileName: z.string().trim().min(1).max(200),
  contentType: z.string().trim().min(1).max(120),
  contentLength: z.number().int().min(1),
};

/** kind decide a política de validação: PROSTHESIS_ATTACHMENT mantém a allowlist legada de MIME
 *  (mantém o anexo de serviço restrito a imagem/PDF); GENERIC é o Drive — qualquer tipo de
 *  arquivo, só limitado por tamanho (upload de qualquer tipo de arquivo é requisito do módulo). */
export const fileRequestUploadSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("PROSTHESIS_ATTACHMENT"),
    ...fileRequestUploadBaseFields,
    contentType: z.enum(ALLOWED_ATTACHMENT_MIME_TYPES),
    contentLength: z.number().int().min(1).max(MAX_ATTACHMENT_BYTES),
  }),
  z.object({
    kind: z.literal("GENERIC"),
    ...fileRequestUploadBaseFields,
    contentLength: z.number().int().min(1).max(MAX_GENERIC_SINGLE_SHOT_BYTES),
  }),
]);
export type FileRequestUploadInput = z.infer<typeof fileRequestUploadSchema>;

/** Fluxo multipart — só pra kind GENERIC (arquivo grande do Drive, ex: STL). */
export const fileMultipartInitInputSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  contentType: z.string().trim().min(1).max(120),
  contentLength: z.number().int().min(1).max(MAX_MULTIPART_BYTES),
});
export type FileMultipartInitInput = z.infer<typeof fileMultipartInitInputSchema>;

export const fileMultipartPartUrlInputSchema = z.object({
  fileId: z.string().min(1),
  uploadId: z.string().min(1),
  partNumber: z.number().int().min(1),
});

export const fileMultipartCompleteInputSchema = z.object({
  fileId: z.string().min(1),
  uploadId: z.string().min(1),
  parts: z.array(z.object({ partNumber: z.number().int().min(1), etag: z.string().min(1) })).min(1),
});

export const fileIdInputSchema = z.object({ fileId: z.string().min(1) });

/** Entidades que um File pode estar relacionado — módulo de Arquivos (Onda D). */
export const fileRelatedTypeSchema = z.enum([
  "PATIENT",
  "PARTNER",
  "PROSTHESIS_ORDER",
  "PROSTHESIS_SERVICE",
  "NOTA_FISCAL",
]);
export type FileRelatedType = z.infer<typeof fileRelatedTypeSchema>;

/** Canais por onde um anexo (ex: arquivo de pedido de prótese) já foi enviado fora da
 *  plataforma — múltipla escolha. Lista livre (não enum de banco) pra permitir adicionar marca
 *  nova sem migration; os 4 primeiros são os "em destaque" na UI (o resto fica atrás de
 *  "Mostrar mais"). */
export const PROSTHESIS_ATTACHMENT_CHANNELS = [
  "Email",
  "WhatsApp",
  "iTero",
  "3Shape",
  "Dentsply Sirona",
  "Medit",
  "Carestream / DEXIS",
  "Straumann",
  "Planmeca",
  "Shining 3D",
  "Panda Scanner",
  "Launca",
  "Dabi",
  "Kramer",
  "Plandent",
  "Dental Wing",
] as const;
export type ProsthesisAttachmentChannel = (typeof PROSTHESIS_ATTACHMENT_CHANNELS)[number];
export const PROSTHESIS_ATTACHMENT_CHANNELS_FEATURED_COUNT = 4;

export const fileEntityRelationInputSchema = z.object({
  fileId: z.string().min(1),
  relatedType: fileRelatedTypeSchema,
  relatedId: z.string().min(1),
});
export type FileEntityRelationInput = z.infer<typeof fileEntityRelationInputSchema>;

export const fileListByEntityInputSchema = z.object({
  relatedType: fileRelatedTypeSchema,
  relatedId: z.string().min(1),
});

export const fileRenameInputSchema = z.object({
  fileId: z.string().min(1),
  name: z.string().trim().min(1).max(200),
});

export const fileMoveInputSchema = z.object({
  fileId: z.string().min(1),
  folderId: z.string().min(1).nullable(),
});

export const folderCreateInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  parentId: z.string().min(1).nullable().optional(),
});

export const folderListInputSchema = z.object({
  folderId: z.string().min(1).nullable().optional(),
});

export const folderIdInputSchema = z.object({ folderId: z.string().min(1) });

export const folderRenameInputSchema = z.object({
  folderId: z.string().min(1),
  name: z.string().trim().min(1).max(120),
});

export const folderMoveInputSchema = z.object({
  folderId: z.string().min(1),
  parentId: z.string().min(1).nullable(),
});

export const fileListRelatedEntitiesInputSchema = z.object({
  relatedType: fileRelatedTypeSchema,
});

export const tagCreateInputSchema = z.object({
  name: z.string().trim().min(1).max(40),
});

export const fileTagInputSchema = z.object({
  fileId: z.string().min(1),
  tagId: z.string().min(1),
});

export const fileShareScopeSchema = z.enum(["USER", "ORGANIZATION", "LINK"]);
export type FileShareScope = z.infer<typeof fileShareScopeSchema>;

/** TTL aplicado a share de LINK quando o cliente não escolhe um. Link de LINK é URL pública sem
 *  autenticação pra arquivo clínico: sem prazo ele sobrevive à saída do funcionário que criou e não
 *  aparece em lugar nenhum como acesso ativo. 30 dias cobre o uso real (mandar exame pra alguém)
 *  sem virar exposição permanente. Scope USER/ORGANIZATION não usa isso — ali o acesso já é
 *  revogável pela própria relação e exige sessão. */
export const LINK_SHARE_DEFAULT_TTL_DAYS = 30;

export const fileShareCreateInputSchema = z.object({
  fileId: z.string().min(1),
  scope: fileShareScopeSchema,
  targetUserId: z.string().min(1).optional(),
  targetOrgId: z.string().min(1).optional(),
  expiresAt: z.coerce.date().optional(),
});

export const fileShareIdInputSchema = z.object({ shareId: z.string().min(1) });

export const fileListSharesInputSchema = z.object({ fileId: z.string().min(1) });

export const fileShareTokenInputSchema = z.object({ token: z.string().min(1) });

export const fileListActivityInputSchema = z.object({ fileId: z.string().min(1) });

export const fileSearchInputSchema = z.object({
  query: z.string().trim().max(200).optional(),
  patientId: z.string().min(1).optional(),
  partnerId: z.string().min(1).optional(),
  orderId: z.string().min(1).optional(),
  serviceId: z.string().min(1).optional(),
  kind: fileKindSchema.optional(),
  tagId: z.string().min(1).optional(),
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
});
export type FileSearchInput = z.infer<typeof fileSearchInputSchema>;
